"use client";

import { useState, useRef, useEffect } from "react";
import { MotionDiv, AnimatePresence } from "@/components/ui/MotionDiv";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { BiCross } from "@/assets/icons/bi-cross";
import { TablerHomeStar } from "@/assets/icons/tabler-home-star";
import background from "@/assets/images/homepage/background.png";

const navLinks = [
  { name: "Le lieu", href: "/#place" },
  { name: "Le concept", href: "/#concept" },
  { name: "Les équipements", href: "/#equipments" },
  { name: "Nos offres", href: "/#offers" },
  // { name: "Contact", href: "/#contact" },
];

interface HeaderProps {
  className?: string;
}

export const Header = ({ className = "" }: HeaderProps) => {
  const router = useRouter();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPassedHero, setIsPassedHero] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const headerRef = useRef<HTMLDivElement>(null);
  const lastScrollY = useRef(0);
  const [mounted, setMounted] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    document.body.style.overflow = isMenuOpen ? "" : "hidden";
  };

  useEffect(() => {
    setMounted(true);
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsVisible(currentScrollY < lastScrollY.current || currentScrollY < 50);
      setIsPassedHero(currentScrollY > 50);
      lastScrollY.current = currentScrollY;
    };

    handleScroll(); // Initial check
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.body.style.overflow = "";
    };
  }, []);

  if (!mounted) {
    return null; // ou un placeholder/skeleton
  }

  const menuVariants = {
    closed: {
      opacity: 0,
      transition: { duration: 0.5 },
    },
    open: {
      opacity: 1,
      transition: { duration: 0.5 },
    },
  };

  const contentVariants = {
    closed: { y: 50, opacity: 0 },
    open: {
      y: 0,
      opacity: 1,
      transition: {
        delay: 0.3,
        duration: 0.8,
        ease: "easeInOut",
      },
    },
  };

  const linkVariants = {
    closed: { y: 20, opacity: 0 },
    open: (i: number) => ({
      y: 0,
      opacity: 1,
      transition: { delay: 0.5 + i * 0.1, duration: 0.5 },
    }),
  };

  return (
    <>
      <header
        ref={headerRef}
        className={`site-header fixed left-0 right-0 z-40 px-4 py-4 transition-all duration-300 ph:px-24 md:px-48 lg:px-72 xl:px-96 ${className} ${
          isVisible ? "translate-y-0" : "-translate-y-[calc(100%+1rem)]"
        } ${
          isPassedHero && !isMenuOpen
            ? "top-0 h-32 bg-primary/80 text-white"
            : "!h-fit bg-transparent py-8 text-white"
        }`}
      >
        <div className="flex h-full items-center justify-between">
          <Link href="/" onClick={() => isMenuOpen && toggleMenu()}>
            <h1 className="text-3xl font-bold">Espace Cadet</h1>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden items-center gap-6 xl:flex">
            {navLinks.map((item) => (
              <Link
                key={item.name}
                href={item.href}
                className="text-md whitespace-nowrap uppercase tracking-wider transition-colors duration-300 hover:cursor-pointer hover:text-black"
              >
                {item.name}
              </Link>
            ))}
          </div>

          <Link href="/#contact">
            <div className="group hidden items-center gap-2 hover:cursor-pointer xl:flex">
              <TablerHomeStar className="h-6 w-6 text-white transition-colors duration-300 group-hover:text-black" />
              <p className="text-md uppercase tracking-wider text-white transition-colors duration-300 group-hover:text-black">
                Contactez-nous
              </p>
            </div>
          </Link>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="z-50 xl:hidden"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <BiCross className="h-8 w-8 text-white" />
            ) : (
              <div className="flex flex-col gap-2">
                <span className="block h-[2px] w-8 bg-white transition-all duration-300" />
                <span className="block h-[2px] w-8 bg-white transition-all duration-300" />
                <span className="block h-[2px] w-8 bg-white transition-all duration-300" />
              </div>
            )}
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <MotionDiv
            className="fixed inset-0 z-30 bg-cover bg-center bg-no-repeat xl:hidden"
            style={{ backgroundImage: `url(${background.src})` }}
            initial="closed"
            animate="open"
            exit="closed"
            variants={menuVariants}
          >
            <MotionDiv
              className="flex h-full flex-col items-center justify-center px-4"
              variants={contentVariants}
            >
              <div className="flex w-full flex-col items-center space-y-8 text-center">
                {navLinks.map((item, index) => (
                  <MotionDiv
                    key={item.name}
                    custom={index}
                    variants={linkVariants}
                  >
                    <Link
                      href={item.href}
                      className="block text-2xl font-normal text-white transition-colors duration-300 [text-shadow:_0_1px_2px_rgb(255_255_255_/_0.2)]"
                      onClick={() => {
                        toggleMenu();
                      }}
                    >
                      {item.name}
                    </Link>
                  </MotionDiv>
                ))}

                {/* Ajout du bouton de réservation */}
                <MotionDiv custom={navLinks.length} variants={linkVariants}>
                  <div className="mt-6 flex items-center gap-2">
                    <TablerHomeStar className="h-6 w-6 text-white" />
                    <p className="text-2xl font-normal text-white [text-shadow:_0_1px_2px_rgb(255_255_255_/_0.2)]">
                      Contactez-nous
                    </p>
                  </div>
                </MotionDiv>
              </div>
            </MotionDiv>
          </MotionDiv>
        )}
      </AnimatePresence>
    </>
  );
};
